import { CheckOutlined, PlusOutlined, UserAddOutlined } from '@ant-design/icons';
import { type SelectProps, ConfigProvider, Divider } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce';

import { Modal, useMessage } from 'components/ui';
import { useAccountContext } from 'utils/account/AccountContext';
import { useOrganizationRelationships } from 'utils/organization';
import { notEmpty } from 'utils/types';

import { AddVendorContainer, VendorSelect } from './VendorOrgChooser.style';

interface Props extends SelectProps {
  // Vendor - work orders; delivery - inspections;
  relationshipType?: 'vendor' | 'delivery';
  includeCurrentOrg?: boolean;
}

export function VendorOrgChooser({
  // Default to vendor
  relationshipType = 'vendor',
  includeCurrentOrg = false,
  ...props
}: Props) {
  const { t } = useTranslation();
  const message = useMessage();
  const { customer } = useAccountContext();
  const { errors, loading, relationships } = useOrganizationRelationships({
    types: [relationshipType],
  });
  const [open, _setOpen] = useState<boolean>(false);

  const errorsString = errors?.map(e => e.message).join('. ');

  useEffect(() => {
    if (!loading && errorsString) {
      message.warning(
        relationshipType === 'delivery'
          ? t(
              'vendors.unableToLoadDeliveryOrganizations',
              'Unable to load delivery organizations. {{errorsString}}',
              { errorsString }
            )
          : t('vendors.unableToLoadVendors', 'Unable to load vendors. {{errorsString}}', {
              errorsString,
            }),
        8
      );
    }
  }, [errorsString, loading, message, relationshipType, t]);

  const vendorOptions = useMemo(() => {
    const options = (relationships ?? [])
      .map(r => r.relationshipPartner)
      .filter(notEmpty)
      .map(v => ({ label: v.name, text: v.name, value: v.id }));

    if (includeCurrentOrg && customer && !options.some(o => o.value === customer.id)) {
      options.unshift({ label: customer.name, text: customer.name, value: customer.id });
    }

    return options;
  }, [customer, includeCurrentOrg, relationships]);

  // small wait to let the antd dropdown animations get outta the way
  const setOpen = useDebouncedCallback((value: boolean) => _setOpen(value), 100);

  return (
    <ConfigProvider renderEmpty={() => <RenderEmpty />}>
      <Modal
        data-testid="add-vendor-modal"
        footer={null}
        onCancel={() => setOpen(false)}
        open={open}
        title={t('components.addVendor', 'Add Vendor')}
      >
        {/*
          The relationship support has to establish differs per `relationshipType`: a `vendor`
          relationship is what Work Orders read, a `delivery` relationship is what Inspections
          read, and adding the wrong one would not make the missing Organization selectable here.
          Both keys are written out in full rather than built dynamically so `usedKeys.test.ts`
          can still see them.
        */}
        {relationshipType === 'delivery' ? (
          <Trans
            i18nKey="components.deliveryOrgSupportRequest"
            t={t}
            defaults="Please contact <supportEmail>support@skyspecs.com</supportEmail> to add an available delivery Organization to your account. Once that relationship has been established, you will be able to attach that Organization to your Inspections."
            components={{
              // content is injected by Trans at runtime
              // eslint-disable-next-line jsx-a11y/anchor-has-content
              supportEmail: <a href="mailto:support@skyspecs.com" />,
            }}
          />
        ) : (
          <Trans
            i18nKey="components.vendorSupportRequest"
            t={t}
            defaults="Please contact <supportEmail>support@skyspecs.com</supportEmail> to add an available vendor to your account. Once that relationship has been established, you will be able to attach that vendor to your work orders."
            components={{
              // content is injected by Trans at runtime
              // eslint-disable-next-line jsx-a11y/anchor-has-content
              supportEmail: <a href="mailto:support@skyspecs.com" />,
            }}
          />
        )}
      </Modal>

      <VendorSelect
        data-testid="add-vendor-select"
        allowClear={true}
        defaultActiveFirstOption={false}
        popupRender={content => (
          <RenderDropdown content={content} setOpen={open => setOpen(open)} />
        )}
        loading={loading}
        menuItemSelectedIcon={<CheckOutlined />}
        options={vendorOptions}
        placeholder={t('components.setVendor', 'Set Vendor')}
        popupMatchSelectWidth={false}
        {...props}
      />
    </ConfigProvider>
  );
}

interface RenderDropdownProps {
  content: React.ReactNode;
  setOpen: (open: boolean) => void;
}

function RenderDropdown({ content, setOpen }: RenderDropdownProps) {
  const { t } = useTranslation();
  return (
    <div id="vendor-chooser-dropdown">
      <div
        data-testid="add-vendor"
        // have to use onMouseDown rather than onClick cuz antd
        onMouseDown={() => setOpen(true)}
      >
        <AddVendorContainer>
          <PlusOutlined style={{ marginRight: '4px' }} />
          <span>{t('components.addVendor', 'Add Vendor')}</span>
        </AddVendorContainer>
      </div>

      <Divider style={{ margin: '4px 0' }} />

      {content}
    </div>
  );
}

function RenderEmpty() {
  const { t } = useTranslation();
  return (
    <div>
      <UserAddOutlined style={{ marginRight: '4px' }} />
      <span>{t('components.noVendors', 'No vendors')}</span>
    </div>
  );
}
